import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useErrand } from '../contexts/ErrandContext';
import { supabase } from '../lib/supabase';
import { ErrandCardSkeleton } from '../components/UI/LoadingSkeleton';
import EmptyState from '../components/UI/EmptyState';
import RecurringErrandForm, { RecurringErrandData } from '../components/Errand/RecurringErrandForm';
import { showSuccess, showError } from '../utils/toast';
import { getErrorMessage } from '../utils/errorMessages';

interface RecurringErrand {
  id: string;
  title: string;
  description?: string;
  frequency: 'daily' | 'weekly' | 'monthly';
  day_of_week?: number;
  day_of_month?: number;
  time_of_day: string;
  next_run_date: string;
  last_run_date?: string;
  is_active: boolean;
  errand_types?: {
    name: string;
  };
}

const RecurringErrandsPage: React.FC = () => {
  const { user } = useAuth();
  const { errandTypes, createRecurringErrand } = useErrand();
  const [recurringErrands, setRecurringErrands] = useState<RecurringErrand[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingErrand, setEditingErrand] = useState<RecurringErrand | null>(null);

  useEffect(() => {
    if (user) {
      fetchRecurringErrands();
    }
  }, [user]);

  const fetchRecurringErrands = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('recurring_errands')
        .select(`
          *,
          errand_types (name)
        `)
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setRecurringErrands(data || []);
    } catch (error) {
      console.error('Error fetching recurring errands:', error);
      showError(getErrorMessage(error, 'Failed to load recurring errands'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (data: RecurringErrandData) => {
    try {
      const success = await createRecurringErrand(data);
      if (success) {
        showSuccess('Recurring errand created successfully!');
        setShowForm(false);
        setEditingErrand(null);
        await fetchRecurringErrands();
      }
    } catch (error) {
      console.error('Error creating recurring errand:', error);
      showError(getErrorMessage(error, 'Failed to create recurring errand'));
    }
  };

  const handleToggleActive = async (id: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('recurring_errands')
        .update({ is_active: !currentStatus })
        .eq('id', id);

      if (error) throw error;
      showSuccess(`Recurring errand ${!currentStatus ? 'activated' : 'deactivated'}`);
      await fetchRecurringErrands();
    } catch (error) {
      console.error('Error toggling recurring errand:', error);
      showError('Failed to update recurring errand');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this recurring errand?')) return;

    try {
      const { error } = await supabase
        .from('recurring_errands')
        .delete()
        .eq('id', id);

      if (error) throw error;
      showSuccess('Recurring errand deleted successfully');
      await fetchRecurringErrands();
    } catch (error) {
      console.error('Error deleting recurring errand:', error);
      showError('Failed to delete recurring errand');
    }
  };

  const formatFrequency = (frequency: string, dayOfWeek?: number, dayOfMonth?: number) => {
    if (frequency === 'daily') return 'Daily';
    if (frequency === 'weekly') {
      const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      return `Weekly (${days[dayOfWeek || 0]})`;
    }
    if (frequency === 'monthly') {
      return `Monthly (Day ${dayOfMonth || 1})`;
    }
    return frequency;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-7xl mx-auto">
          <div className="space-y-4">
            <ErrandCardSkeleton />
            <ErrandCardSkeleton />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Recurring Errands</h1>
            <p className="text-gray-600">Set up errands that repeat automatically</p>
          </div>
          <button
            onClick={() => {
              setEditingErrand(null);
              setShowForm(true);
            }}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            + Create Recurring Errand
          </button>
        </div>

        {showForm && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              {editingErrand ? 'Edit Recurring Errand' : 'Create Recurring Errand'}
            </h2>
            <RecurringErrandForm
              onSubmit={handleSubmit}
              onCancel={() => {
                setShowForm(false);
                setEditingErrand(null);
              }}
              initialData={editingErrand ? {
                errandTypeId: '',
                title: editingErrand.title,
                description: editingErrand.description,
                frequency: editingErrand.frequency,
                dayOfWeek: editingErrand.day_of_week,
                dayOfMonth: editingErrand.day_of_month,
                timeOfDay: editingErrand.time_of_day
              } : undefined}
            />
          </div>
        )}

        {recurringErrands.length === 0 ? (
          <EmptyState
            icon="🔄"
            title="No Recurring Errands"
            message="Create recurring errands to automate your regular errand requests."
            actionLabel="Create Recurring Errand"
            onAction={() => setShowForm(true)}
          />
        ) : (
          <div className="space-y-4">
            {recurringErrands.map((errand) => (
              <div
                key={errand.id}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">{errand.title}</h3>
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium ${
                          errand.is_active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {errand.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    {errand.description && (
                      <p className="text-gray-600 mb-3">{errand.description}</p>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                      <div>
                        <span className="font-medium">Type:</span> {errand.errand_types?.name || 'N/A'}
                      </div>
                      <div>
                        <span className="font-medium">Frequency:</span>{' '}
                        {formatFrequency(errand.frequency, errand.day_of_week, errand.day_of_month)}
                      </div>
                      <div>
                        <span className="font-medium">Time:</span> {errand.time_of_day}
                      </div>
                      <div>
                        <span className="font-medium">Next Run:</span>{' '}
                        {new Date(errand.next_run_date).toLocaleDateString('en-BW', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </div>
                      {errand.last_run_date && (
                        <div>
                          <span className="font-medium">Last Run:</span>{' '}
                          {new Date(errand.last_run_date).toLocaleDateString('en-BW', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col space-y-2 ml-4">
                    <button
                      onClick={() => handleToggleActive(errand.id, errand.is_active)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        errand.is_active
                          ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                          : 'bg-green-100 text-green-800 hover:bg-green-200'
                      }`}
                    >
                      {errand.is_active ? 'Deactivate' : 'Activate'}
                    </button>
                    <button
                      onClick={() => handleDelete(errand.id)}
                      className="px-4 py-2 bg-red-100 text-red-800 rounded-lg text-sm font-medium hover:bg-red-200 transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RecurringErrandsPage;



